/**
 * Simple Dependency Analyzer
 * 
 * Infrastructure adapter implementing dependency analysis using package.json parsing.
 * Analyzes package dependencies and builds dependency graphs.
 */

import { injectable } from 'inversify';
import { promises as fs } from 'fs';
import * as path from 'path';
import { IDependencyAnalyzer } from '../../application/services/BoundedContextAnalysisService';

@injectable()
export class SimpleDependencyAnalyzer implements IDependencyAnalyzer {
  
  async analyzeDependencies(packages: string[]): Promise<{
    dependencyGraph: {
      nodes: Array<{ id: string; name: string; type: 'core' | 'supporting' | 'generic'; complianceScore: number; }>;
      edges: Array<{ source: string; target: string; type: 'depends-on' | 'publishes-to' | 'subscribes-to'; }>;
      cycles: string[][];
    };
  }> {
    try {
      const packageData = await this.loadPackageData(packages);
      const nodes = this.buildNodes(packageData);
      const edges = this.buildEdges(packageData);
      const cycles = this.detectCycles(nodes, edges);
      
      return {
        dependencyGraph: {
          nodes,
          edges,
          cycles,
        },
      };
    } catch (error) {
      throw new Error(`Failed to analyze dependencies: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private async loadPackageData(packages: string[]): Promise<Map<string, {
    packageName: string;
    dependencies: string[];
    contextType: 'core' | 'supporting' | 'generic';
  }>> {
    const packageData = new Map();
    
    for (const packagePath of packages) {
      try {
        const packageJsonPath = path.join(packagePath, 'package.json');
        const packageJson = JSON.parse(await fs.readFile(packageJsonPath, 'utf-8'));
        
        const packageName = packageJson.name || path.basename(packagePath);
        const dependencies = this.extractSwoftDependencies(packageJson);
        const contextType = this.determineContextType(packageName);
        
        packageData.set(packageName, {
          packageName,
          dependencies,
          contextType,
        });
      } catch (error) {
        console.warn(`Failed to load package data for ${packagePath}:`, error);
      }
    }
    
    return packageData;
  }

  private buildNodes(packageData: Map<string, any>): Array<{ 
    id: string; 
    name: string; 
    type: 'core' | 'supporting' | 'generic'; 
    complianceScore: number; 
  }> {
    const nodes: Array<{ id: string; name: string; type: 'core' | 'supporting' | 'generic'; complianceScore: number; }> = [];
    
    for (const [packageName, data] of packageData) {
      nodes.push({
        id: packageName,
        name: this.extractDisplayName(packageName),
        type: data.contextType,
        complianceScore: this.calculateNodeComplianceScore(data),
      });
    }
    
    return nodes;
  }

  private buildEdges(packageData: Map<string, any>): Array<{ 
    source: string; 
    target: string; 
    type: 'depends-on' | 'publishes-to' | 'subscribes-to'; 
  }> {
    const edges: Array<{ source: string; target: string; type: 'depends-on' | 'publishes-to' | 'subscribes-to'; }> = [];
    
    for (const [packageName, data] of packageData) {
      for (const dependency of data.dependencies) {
        if (packageData.has(dependency)) {
          edges.push({
            source: packageName,
            target: dependency,
            type: 'depends-on',
          });
        }
      }
    }
    
    return edges;
  }

  private detectCycles(
    nodes: Array<{ id: string; name: string; type: string; complianceScore: number; }>,
    edges: Array<{ source: string; target: string; type: string; }>
  ): string[][] {
    const graph = new Map<string, string[]>();
    
    // Build adjacency list
    for (const node of nodes) {
      graph.set(node.id, []);
    }
    
    for (const edge of edges) {
      const neighbors = graph.get(edge.source) || [];
      neighbors.push(edge.target);
      graph.set(edge.source, neighbors);
    }
    
    // Detect cycles using DFS
    const visited = new Set<string>();
    const recursionStack = new Set<string>();
    const cycles: string[][] = [];
    
    const dfs = (node: string, path: string[]): void => {
      if (recursionStack.has(node)) {
        // Found a cycle
        const cycleStart = path.indexOf(node);
        if (cycleStart !== -1) {
          cycles.push([...path.slice(cycleStart), node]);
        }
        return;
      }
      
      if (visited.has(node)) {
        return;
      }
      
      visited.add(node);
      recursionStack.add(node);
      path.push(node);
      
      const neighbors = graph.get(node) || [];
      for (const neighbor of neighbors) {
        dfs(neighbor, [...path]);
      }
      
      recursionStack.delete(node);
    };
    
    for (const node of nodes) {
      if (!visited.has(node.id)) {
        dfs(node.id, []);
      }
    }
    
    return cycles;
  }

  private extractSwoftDependencies(packageJson: any): string[] {
    const allDeps = [
      ...Object.keys(packageJson.dependencies || {}),
      ...Object.keys(packageJson.devDependencies || {}),
    ];
    
    return allDeps.filter(dep => dep.startsWith('@swoft/'));
  }

  private determineContextType(packageName: string): 'core' | 'supporting' | 'generic' {
    const corePackages = [
      'party-manager', 'business-strategy', 'product-design-domain',
      'gtd-domain', 'ddd', 'ui-ux-design-domain'
    ];
    
    const supportingPackages = [
      'dev-ops', 'mongodb-toolkit', 'core-architecture', 'core-errors'
    ];
    
    if (corePackages.some(pkg => packageName.includes(pkg))) {
      return 'core';
    }
    
    if (supportingPackages.some(pkg => packageName.includes(pkg))) {
      return 'supporting';
    }
    
    return 'generic';
  }

  private extractDisplayName(packageName: string): string {
    const parts = packageName.split('/');
    const name = parts[parts.length - 1];
    return name.split('-').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
  }

  private calculateNodeComplianceScore(data: any): number {
    // Simple scoring based on dependency count and type
    const depCount = data.dependencies.length;
    
    if (data.contextType === 'core') {
      // Core contexts should have fewer dependencies
      return Math.max(0, 100 - (depCount * 10));
    }
    
    if (data.contextType === 'supporting') {
      // Supporting contexts can have more dependencies
      return Math.max(0, 100 - (depCount * 5));
    }
    
    // Generic contexts
    return Math.max(0, 100 - (depCount * 15));
  }
}